import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, ChevronDown } from "lucide-react";
import { api } from "../lib/api";

export interface UnitComboboxProps {
  value: string;
  onChange: (unit: string) => void;
}

/**
 * Padajuća lista mernih jedinica (kom, kg, m, usluga...) koja se čuva po
 * kompaniji preko /api/units. Svaka stavka ima "x" za brisanje iz liste
 * ponude (ne diraju se artikli koji je već koriste). Ako korisnik ukuca
 * nešto što ne postoji, dugme "Dodaj" je odmah snima preko API-ja i bira.
 */
export function UnitCombobox({ value, onChange }: UnitComboboxProps) {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => setInputValue(value), [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setInputValue(value);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  const { data: units } = useQuery({
    queryKey: ["units"],
    queryFn: () => api.listUnits(),
  });

  const createUnit = useMutation({
    mutationFn: (name: string) => api.createUnit({ name }),
    onSuccess: (unit) => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      onChange(unit.name);
      setInputValue(unit.name);
      setIsOpen(false);
    },
  });

  const deleteUnit = useMutation({
    mutationFn: (id: string) => api.deleteUnit(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["units"] }),
  });

  const filtered = (units ?? []).filter((u) =>
    u.name.toLowerCase().includes(inputValue.toLowerCase()),
  );
  const exactMatch = (units ?? []).some(
    (u) => u.name.toLowerCase() === inputValue.trim().toLowerCase(),
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="flex h-10 w-full items-center justify-between rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>{value || "kom"}</span>
        <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-surface shadow-card">
          <input
            autoFocus
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Izaberi ili upiši novu jedinicu mere"
            className="w-full border-b border-border px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-48 overflow-auto">
            {filtered.map((unit) => (
              <li key={unit.id} className="flex items-center justify-between px-1">
                <button
                  type="button"
                  onClick={() => {
                    onChange(unit.name);
                    setInputValue(unit.name);
                    setIsOpen(false);
                  }}
                  className="flex-1 rounded-md px-2 py-2 text-left text-sm hover:bg-brand-light"
                >
                  {unit.name}
                </button>
                <button
                  type="button"
                  aria-label={`Ukloni jedinicu ${unit.name}`}
                  onClick={() => deleteUnit.mutate(unit.id)}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
          {inputValue.trim() && !exactMatch && (
            <button
              type="button"
              onClick={() => createUnit.mutate(inputValue.trim())}
              className="w-full border-t border-border px-3 py-2 text-left text-sm font-medium text-brand hover:bg-brand-light"
            >
              + Dodaj "{inputValue.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
